/**
 * ChunkedUploader - JavaScript library for chunked file uploads
 *
 * Features:
 * - Upload large files (up to 4+ GB) in chunks
 * - Resume uploads after connection interruption
 * - Progress tracking with callbacks
 * - Automatic retry with exponential backoff
 * - LocalStorage state persistence for resume capability
 *
 * @version 1.0.0
 */

class ChunkedUploader {
  /**
   * Create a ChunkedUploader instance
   * @param {Object} options Configuration options
   */
  constructor(options = {}) {
    // Default configuration
    this.endpoint = options.endpoint || '/back/index.php';
    this.chunkSize = options.chunkSize || 5 * 1024 * 1024; // 5 MB default
    this.maxFileSize = options.maxFileSize || 4 * 1024 * 1024 * 1024; // 4 GB default
    this.maxRetries = options.maxRetries || 5;
    this.retryDelay = options.retryDelay || 2000; // 2 second base delay
    this.headers = options.headers || {};

    // Callbacks
    this.onProgress = options.onProgress || (() => {});
    this.onChunkProgress = options.onChunkProgress || (() => {}); // Progress within current chunk
    this.onChunkComplete = options.onChunkComplete || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || (() => {});
    this.onPause = options.onPause || (() => {});
    this.onResume = options.onResume || (() => {});

    // State
    this.uploadId = null;
    this.file = null;
    this.metadata = {};
    this.totalChunks = 0;
    this.uploadedChunks = [];
    this.bytesUploaded = 0;
    this.isUploading = false;
    this.isPaused = false;
    this.isAborted = false;
    this.currentChunkIndex = 0;
  }

  /**
   * Start uploading a file
   * @param {File} file The file to upload
   * @param {Object} metadata Additional metadata to send with the upload
   * @returns {Promise<Object>} Upload result
   */
  async upload(file, metadata = {}) {
    if (this.isUploading) {
      throw new Error('Upload already in progress');
    }

    // Validate file size
    if (file.size > this.maxFileSize) {
      const error = new Error(`File size exceeds maximum allowed (${this.formatBytes(this.maxFileSize)})`);
      this.onError(error, false);
      throw error;
    }

    this.file = file;
    this.metadata = metadata;
    this.isUploading = true;
    this.isPaused = false;
    this.isAborted = false;
    this.uploadedChunks = [];
    this.bytesUploaded = 0;

    try {
      // Initialize upload session
      const initResult = await this.initializeUpload();
      if (!initResult.success) {
        throw new Error(initResult.error || 'Failed to initialize upload');
      }

      this.uploadId = initResult.upload_id;
      this.totalChunks = initResult.total_chunks;
      this.chunkSize = initResult.chunk_size;

      // Save state for resume capability
      this.saveState();

      // Upload all chunks
      await this.uploadAllChunks();

      if (this.isAborted) {
        return { success: false, aborted: true };
      }

      // Guard: complete() must NEVER be called with missing chunks. The
      // server then reports `FAIL missing_chunks` and the user sees a
      // generic error. uploadAllChunks() exits early on isPaused — before
      // this guard the code would still call complete and produce a
      // half-uploaded merge attempt. Same goes for isPaused on resume().
      if (this.isPaused) {
        return { success: false, paused: true };
      }
      if (this.uploadedChunks.length < this.totalChunks) {
        throw new Error('Upload incomplete: ' + this.uploadedChunks.length + '/' + this.totalChunks + ' chunks');
      }

      // Complete the upload
      const completeResult = await this.completeUpload();

      // Clear saved state
      this.clearState();

      this.isUploading = false;
      this.onComplete(completeResult);

      return completeResult;

    } catch (error) {
      this.isUploading = false;
      const canResume = this.uploadId && !this.isAborted;
      this.onError(error, canResume);
      throw error;
    }
  }

  /**
   * Pause the current upload
   */
  pause() {
    if (this.isUploading && !this.isPaused) {
      this.isPaused = true;
      this.saveState();
      this.onPause();
    }
  }

  /**
   * Resume a paused or errored upload
   */
  async resume() {
    if (this.isAborted || !this.uploadId) {
      return;
    }

    this.isPaused = false;
    this.isUploading = true;
    this.onResume();

    try {
      await this.uploadAllChunks();

      if (!this.isAborted && !this.isPaused) {
        // Same invariant guard as in upload(): never call complete with
        // missing chunks (server returns FAIL missing_chunks → generic
        // user error). If we got here with the loop drained but not all
        // chunks present, treat it as an error so onError fires.
        if (this.uploadedChunks.length < this.totalChunks) {
          throw new Error('Upload incomplete: ' + this.uploadedChunks.length + '/' + this.totalChunks + ' chunks');
        }
        const completeResult = await this.completeUpload();
        this.clearState();
        this.isUploading = false;
        this.onComplete(completeResult);
        return completeResult;
      }
    } catch (error) {
      this.isUploading = false;
      const canResume = this.uploadId && !this.isAborted;
      this.onError(error, canResume);
      throw error;
    }
  }

  /**
   * Resume upload from saved state (e.g., after page reload)
   * @param {string|Object} savedState Saved state from localStorage or previous session
   * @param {File} file The original file (must be provided again after page reload)
   * @returns {Promise<Object>} Upload result
   */
  async resumeFrom(savedState, file) {
    if (typeof savedState === 'string') {
      savedState = JSON.parse(savedState);
    }

    if (!savedState || !savedState.uploadId) {
      throw new Error('Invalid saved state');
    }

    if (!file) {
      throw new Error('File must be provided for resume');
    }

    // Validate file matches saved state
    if (file.name !== savedState.fileName || file.size !== savedState.fileSize) {
      throw new Error('File does not match saved upload state');
    }

    // Fetch current status from server
    const statusResult = await this.fetchStatus(savedState.uploadId);
    if (!statusResult.success) {
      throw new Error(statusResult.error || 'Upload session not found or expired');
    }

    if (statusResult.is_expired) {
      this.clearState();
      throw new Error('Upload session has expired');
    }

    // Restore state
    this.file = file;
    this.uploadId = savedState.uploadId;
    this.metadata = savedState.metadata || {};
    this.totalChunks = statusResult.total_chunks;
    this.chunkSize = statusResult.chunk_size;
    this.uploadedChunks = statusResult.uploaded_chunks || [];
    this.bytesUploaded = statusResult.bytes_received || 0;
    this.isUploading = true;
    this.isPaused = false;
    this.isAborted = false;

    // Find first missing chunk
    this.currentChunkIndex = this.findFirstMissingChunk();

    // Report progress
    this.onProgress(
      statusResult.progress_percent,
      this.bytesUploaded,
      this.file.size
    );

    // Continue upload
    return this.resume();
  }

  /**
   * Abort the current upload
   */
  async abort() {
    this.isAborted = true;
    this.isPaused = false;
    this.isUploading = false;

    if (this.uploadId) {
      try {
        await this.abortUploadOnServer();
      } catch (e) {
        // Ignore abort errors
        console.warn('Failed to abort upload on server:', e);
      }
    }

    this.clearState();
  }

  /**
   * Get current upload progress
   * @returns {Object} Progress information
   */
  getProgress() {
    if (!this.file) {
      return {
        percent: 0,
        bytesUploaded: 0,
        bytesTotal: 0,
        chunksUploaded: 0,
        chunksTotal: 0
      };
    }

    return {
      percent: this.file.size > 0 ? (this.bytesUploaded / this.file.size) * 100 : 0,
      bytesUploaded: this.bytesUploaded,
      bytesTotal: this.file.size,
      chunksUploaded: this.uploadedChunks.length,
      chunksTotal: this.totalChunks,
      isPaused: this.isPaused,
      isUploading: this.isUploading
    };
  }

  /**
   * Check if there's a resumable upload in localStorage
   * @param {string} storageKey Optional custom storage key
   * @returns {Object|null} Saved state or null
   */
  static getSavedState(storageKey = null) {
    const keys = storageKey
      ? [storageKey]
      : Object.keys(localStorage).filter(k => k.startsWith('chunked_upload_'));

    for (const key of keys) {
      try {
        const state = JSON.parse(localStorage.getItem(key));
        if (state && state.uploadId && state.lastUpdate) {
          // Check if state is not too old (24 hours)
          const age = Date.now() - state.lastUpdate;
          if (age < 24 * 60 * 60 * 1000) {
            return state;
          }
        }
      } catch (e) {
        // Invalid state, ignore
      }
    }

    return null;
  }

  // =========================================================================
  // Private methods
  // =========================================================================

  /**
   * Initialize upload session on server
   */
  async initializeUpload() {
    const payload = {
      action: 'chunked_init',
      file_name: this.file.name,
      file_size: this.file.size,
      file_type: this.file.type,
      chunk_size: this.chunkSize,
      total_chunks: Math.ceil(this.file.size / this.chunkSize),
      metadata: this.metadata
    };

    const response = await this.request('POST', payload);
    return response;
  }

  /**
   * Upload all remaining chunks
   */
  async uploadAllChunks() {
    this.currentChunkIndex = this.findFirstMissingChunk();

    while (this.currentChunkIndex < this.totalChunks) {
      if (this.isPaused || this.isAborted) {
        break;
      }

      // Check if chunk already uploaded
      if (this.uploadedChunks.includes(this.currentChunkIndex)) {
        this.currentChunkIndex++;
        continue;
      }

      await this.uploadChunkWithRetry(this.currentChunkIndex);

      this.currentChunkIndex++;
    }
  }

  /**
   * Upload a single chunk with retry logic
   */
  async uploadChunkWithRetry(chunkIndex) {
    let lastError = null;

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      if (this.isPaused || this.isAborted) {
        return;
      }

      try {
        const result = await this.uploadSingleChunk(chunkIndex);

        if (result.success) {
          // Update state
          if (!this.uploadedChunks.includes(chunkIndex)) {
            this.uploadedChunks.push(chunkIndex);
            this.uploadedChunks.sort((a, b) => a - b);
          }
          this.bytesUploaded = result.total_received;

          // Callbacks
          const percent = (this.bytesUploaded / this.file.size) * 100;
          this.onProgress(percent, this.bytesUploaded, this.file.size);
          this.onChunkComplete(chunkIndex, this.totalChunks);

          // Save state after each successful chunk
          this.saveState();

          return result;
        }

        lastError = new Error(result.error || 'Chunk upload failed');

      } catch (error) {
        lastError = error;
      }

      // Wait before retry (exponential backoff)
      if (attempt < this.maxRetries - 1) {
        await this.sleep(this.retryDelay * Math.pow(2, attempt));
      }
    }

    throw lastError || new Error('Max retries exceeded for chunk ' + chunkIndex);
  }

  /**
   * Upload a single chunk to server
   */
  async uploadSingleChunk(chunkIndex) {
    const start = chunkIndex * this.chunkSize;
    const end = Math.min(start + this.chunkSize, this.file.size);
    const chunk = this.file.slice(start, end);
    const chunkSize = end - start;

    const formData = new FormData();
    formData.append('action', 'chunked_upload');
    formData.append('upload_id', this.uploadId);
    formData.append('chunk_index', chunkIndex);
    formData.append('chunk', chunk);

    // Calculate base bytes (already uploaded chunks)
    const baseBytes = this.bytesUploaded;

    const response = await this.requestFormData(formData, (loaded, total) => {
      // Calculate overall progress including current chunk progress
      const currentChunkProgress = loaded / total;
      const overallBytes = baseBytes + (chunkSize * currentChunkProgress);
      const overallPercent = (overallBytes / this.file.size) * 100;

      // Call chunk progress callback
      this.onChunkProgress(chunkIndex, currentChunkProgress * 100, loaded, total);

      // Call main progress callback with smooth progress
      this.onProgress(overallPercent, overallBytes, this.file.size);
    });
    return response;
  }

  /**
   * Complete upload on server.
   *
   * The merge + checksum + queue-insert runs server-side here. For files up
   * to ~1 GB it currently finishes in <2s, but we cap the wait at 180s so a
   * dropped response packet or stalled connection raises an explicit error
   * the user can act on, instead of hanging indefinitely on a bare fetch().
   *
   * Always logs duration to console (and into thrown Error.message) so the
   * `CHUNKED_UPLOAD_ERROR` diagnostic line captures whether we failed during
   * server work or while waiting on the response.
   */
  async completeUpload() {
    const payload = {
      action: 'chunked_complete',
      upload_id: this.uploadId
    };

    const controller = new AbortController();
    const COMPLETE_TIMEOUT_MS = 180000;
    const t0 = Date.now();
    const timeoutId = setTimeout(() => controller.abort(), COMPLETE_TIMEOUT_MS);

    try {
      const response = await this.request('POST', payload, {}, controller.signal);
      const elapsed = Date.now() - t0;
      // eslint-disable-next-line no-console
      console.log('[chunked] completeUpload done in ' + elapsed + 'ms, success=' + (response && response.success));
      return response;
    } catch (err) {
      const elapsed = Date.now() - t0;
      if (err && err.name === 'AbortError') {
        throw new Error('completeUpload timeout after ' + elapsed + 'ms (limit ' + COMPLETE_TIMEOUT_MS + 'ms)');
      }
      err.message = (err.message || 'completeUpload failed') + ' [after ' + elapsed + 'ms]';
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Fetch upload status from server
   */
  async fetchStatus(uploadId) {
    const response = await this.request('GET', null, {
      action: 'chunked_status',
      upload_id: uploadId
    });
    return response;
  }

  /**
   * Abort upload on server
   */
  async abortUploadOnServer() {
    const payload = {
      action: 'chunked_abort',
      upload_id: this.uploadId
    };

    await this.request('POST', payload);
  }

  /**
   * Find first chunk that hasn't been uploaded
   */
  findFirstMissingChunk() {
    for (let i = 0; i < this.totalChunks; i++) {
      if (!this.uploadedChunks.includes(i)) {
        return i;
      }
    }
    return this.totalChunks;
  }

  /**
   * Make HTTP request
   */
  async request(method, body = null, queryParams = {}, signal = null) {
    let url = this.endpoint;

    // Add query parameters
    const params = new URLSearchParams(queryParams);
    if (params.toString()) {
      url += (url.includes('?') ? '&' : '?') + params.toString();
    }

    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...this.headers
      }
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    if (signal) {
      options.signal = signal;
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      let detail = '';
      try { detail = ': ' + (await response.text()).substring(0, 200); } catch (e) { /* ignore */ }
      throw new Error(`HTTP ${response.status}${detail}`);
    }

    return response.json();
  }

  /**
   * Make HTTP request with FormData and upload progress tracking
   * @param {FormData} formData Form data to upload
   * @param {Function} onProgress Progress callback (loaded, total)
   */
  async requestFormData(formData, onProgress = null) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            onProgress(event.loaded, event.total);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid JSON response: ' + xhr.responseText.substring(0, 200)));
          }
        } else {
          let detail = '';
          try { detail = ': ' + xhr.responseText.substring(0, 200); } catch (e) { /* ignore */ }
          reject(new Error(`HTTP ${xhr.status}${detail}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout (60s)'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('POST', this.endpoint);
      // 60s per chunk: 5MB over a 1 Mbit/s mobile uplink takes ~40s; 30s was too tight for slow networks.
      // We still get 3 retries on top, so a single slow chunk doesn't blow the whole upload.
      xhr.timeout = 60000;

      // Set headers (except Content-Type - browser sets it for FormData)
      Object.entries(this.headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type') {
          xhr.setRequestHeader(key, value);
        }
      });

      xhr.send(formData);
    });
  }

  /**
   * Save state to localStorage for resume capability
   */
  saveState() {
    if (!this.uploadId) return;

    const state = {
      uploadId: this.uploadId,
      fileName: this.file.name,
      fileSize: this.file.size,
      fileType: this.file.type,
      metadata: this.metadata,
      chunkSize: this.chunkSize,
      totalChunks: this.totalChunks,
      uploadedChunks: this.uploadedChunks,
      bytesUploaded: this.bytesUploaded,
      lastUpdate: Date.now()
    };

    try {
      localStorage.setItem('chunked_upload_' + this.uploadId, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save upload state to localStorage:', e);
    }
  }

  /**
   * Clear saved state from localStorage
   */
  clearState() {
    if (this.uploadId) {
      try {
        localStorage.removeItem('chunked_upload_' + this.uploadId);
      } catch (e) {
        // Ignore
      }
    }
  }

  /**
   * Sleep helper
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Format bytes to human-readable string
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Export for different module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ChunkedUploader;
}

export default ChunkedUploader;
